/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import {
  Typography,
  Table,
  InputNumber,
  Input,
  Button,
  Spin,
  message,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { get } from 'lodash';
import moment from 'moment';
import {
  fetchDsrLogPqt,
  updateDsrLogPqt,
} from '../../redux/projectPqtReports/actions';
import { createDsrLog } from '../../redux/dsr/action';

const { Text } = Typography;

const PqtActualValuesPage = (props) => {
  const {
    selectedProject,
    selectedMainline,
    sowDetails,
    selectedDate,
    currentDsrLog,
    dsrLogPqt,
    isDsrLogPqtLoading,
    isDsrLogPqtUpdating,
    lastDsrLogPqtUpdateSuccess,
    onFetchDsrLogPqt,
    onUpdateDsrLogPqt,
    onCreateDsrLog,
    onCancel,
  } = props;

  const [localElements, setLocalElements] = useState([]);
  const [fetchKey, setFetchKey] = useState(0);
  const saveClickedRef = React.useRef(false);

  const projectId = get(selectedProject, 'id');
  // In some DSR flows, `selectedMainline` isn't populated yet when this page mounts.
  // Fall back to the already-created DSR log's sowId so PQT elements can still load.
  const sowId =
    get(selectedMainline, 'id') ??
    get(currentDsrLog, 'sowId') ??
    get(Array.isArray(sowDetails) ? sowDetails[0] : null, 'id') ??
    (() => {
      // Legacy DSR screens sometimes keep these in sessionStorage.
      try {
        const raw = sessionStorage.getItem('sowId');
        return raw != null ? Number(raw) : null;
      } catch (_) {
        return null;
      }
    })();
  const selectedLogDateStr = selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : null;
  const currentLogDateStr =
    currentDsrLog?.logDate ? moment(currentDsrLog.logDate).format('YYYY-MM-DD') : null;

  // Ensure a DSR log exists for the selected date when using the PQT tab/section.
  // (DSRReportDetails normally does this, but PQT can be opened directly.)
  const ensureLogKeyRef = React.useRef('');
  useEffect(() => {
    if (!projectId || !sowId || !selectedLogDateStr) return;
    const key = `${projectId}-${sowId}-${selectedLogDateStr}`;
    if (ensureLogKeyRef.current === key) return;
    // If current log matches date+sow, no need to (re)create.
    const matches =
      String(currentDsrLog?.sowId ?? '') === String(sowId) &&
      String(currentLogDateStr ?? '') === String(selectedLogDateStr);
    if (matches) {
      ensureLogKeyRef.current = key;
      return;
    }
    ensureLogKeyRef.current = key;
    onCreateDsrLog({ projectId, sowId, logDate: selectedLogDateStr });
  }, [projectId, sowId, selectedLogDateStr, currentDsrLog?.sowId, currentLogDateStr, onCreateDsrLog]);

  const fetchPqtData = useCallback(() => {
    // Fetch is driven by selectedDate; DSRReportDetails might not be mounted (section view),
    // so currentDsrLog may still point to a previous date.
    if (!projectId || !selectedDate || !sowId) return;
    const logDate = moment(selectedDate).format('YYYY-MM-DD');
    let userEmail = '';
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      userEmail = user.email || '';
    } catch (_) {}
    onFetchDsrLogPqt({
      logDate,
      userEmail,
      sowId: currentDsrLog?.sowId ?? sowId,
      projectId,
    });
  }, [currentDsrLog?.id, currentDsrLog?.sowId, projectId, sowId, selectedDate, onFetchDsrLogPqt]);

  // Fetch on every mount and whenever fetchKey changes (after save)
  useEffect(() => {
    fetchPqtData();
  }, [fetchPqtData, fetchKey]);

  // When date / project / sow changes, clear local rows while loading the next payload.
  useEffect(() => {
    setLocalElements([]);
  }, [projectId, sowId, selectedDate]);

  // Sync local elements and comments from API response
  useEffect(() => {
    const elements = get(dsrLogPqt, 'elements', []);
    const sorted = [...elements].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    setLocalElements(
      sorted.map((el) => ({
        ...el,
        projectPqtReportElementId: el.projectPqtReportElementId ?? el.projectPqtElementId ?? el.id,
        id: el.projectPqtReportElementId ?? el.projectPqtElementId ?? el.id,
        comments: el.comments ?? null,
      }))
    );
  }, [dsrLogPqt]);

  const handleActualDaysChange = useCallback((projectPqtReportElementId, value) => {
    setLocalElements((prev) =>
      prev.map((el) => {
        const id = el.projectPqtReportElementId ?? el.id;
        if (id !== projectPqtReportElementId) return el;
        const bidDays = Number(el.bidDays) || 0;
        const actualDays = value != null && value !== '' ? Number(value) : null;
        const variance = actualDays != null ? actualDays - bidDays : null;
        return { ...el, actualDays, variance };
      })
    );
  }, []);

  const handleElementCommentsChange = useCallback((projectPqtReportElementId, next) => {
    setLocalElements((prev) =>
      prev.map((el) => {
        const id = el.projectPqtReportElementId ?? el.id;
        if (id !== projectPqtReportElementId) return el;
        const val = next != null ? String(next) : '';
        return { ...el, comments: val.trim() ? val : null };
      })
    );
  }, []);

  const getReportElementId = (el) => el.projectPqtReportElementId ?? el.projectPqtElementId ?? el.id;

  // Refetch after save completes and navigate back on success
  const prevUpdating = React.useRef(false);
  useEffect(() => {
    if (prevUpdating.current && !isDsrLogPqtUpdating) {
      setFetchKey((k) => k + 1);
      if (saveClickedRef.current && lastDsrLogPqtUpdateSuccess === true && onCancel) {
        saveClickedRef.current = false;
        onCancel();
      }
    }
    prevUpdating.current = isDsrLogPqtUpdating;
  }, [isDsrLogPqtUpdating, lastDsrLogPqtUpdateSuccess, onCancel]);

  const handleSave = useCallback(() => {
    if (!currentDsrLog?.id) {
      message.warning('DSR log not loaded. Please select date and SOW.');
      return;
    }
    const elements = localElements.map((el) => ({
      projectPqtElementId: el.projectPqtElementId ?? getReportElementId(el),
      actualDays: Number(el.actualDays ?? 0),
      comments: el.comments ?? null,
    }));
    if (elements.length === 0) {
      message.info('Enter at least one actual days value to save.');
      return;
    }
    saveClickedRef.current = true;
    onUpdateDsrLogPqt({
      dsrLogId: currentDsrLog.id,
      elements,
    });
  }, [currentDsrLog?.id, localElements, onUpdateDsrLogPqt]);

  const columns = [
    {
      title: 'PQT Activities',
      dataIndex: 'elementName',
      key: 'elementName',
      width: 360,
      render: (text) => <Text strong={!!text}>{text || '—'}</Text>,
    },
    {
      title: 'Bid No. of Days',
      dataIndex: 'bidDays',
      key: 'bidDays',
      align: 'center',
      width: 100,
      render: (val) => (val != null ? Number(val) : '—'),
    },
    {
      title: 'Actual Days',
      key: 'actualDays',
      align: 'center',
      width: 120,
      render: (_, record) => {
        const id = getReportElementId(record);
        return (
          <InputNumber
            min={0}
            step={1}
            value={record.actualDays != null ? record.actualDays : undefined}
            placeholder="—"
            onChange={(v) => handleActualDaysChange(id, v)}
            style={{ width: '100%' }}
          />
        );
      },
    },
    {
      title: <div style={{ textAlign: 'center' }}>Comments</div>,
      key: 'comments',
      render: (_, record) => {
        const id = getReportElementId(record);
        return (
          <Input
            value={record.comments ?? ''}
            onChange={(e) => handleElementCommentsChange(id, e.target.value)}
            placeholder="Comments"
            bordered={false}
            style={{ width: '100%' }}
          />
        );
      },
    },
  ];

  const totals = localElements.reduce(
    (acc, el) => {
      const bid = Number(el.bidDays) || 0;
      const today = el.actualDays != null && el.actualDays !== '' ? Number(el.actualDays) : null;
      acc.bid += bid;
      if (today != null) acc.today += today;
      return acc;
    },
    { bid: 0, today: 0 }
  );

  return (
    <div style={{ padding: 24, position: 'relative' }}>
      <Spin
        spinning={isDsrLogPqtLoading}
        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
        tip="Loading PQT data..."
        size="large"
      >
        <div style={{ marginBottom: 24 }}>
          <Table
            rowKey={(r) => r.projectPqtElementId ?? r.projectPqtReportElementId ?? r.elementName ?? r.sortOrder}
            columns={columns}
            dataSource={localElements}
            pagination={false}
            size="small"
            scroll={{ x: 860 }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <Text strong>Total</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="center">
                    <Text strong>{totals.bid}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="center">
                    <Text strong>{totals.today}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} />
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </div>
      </Spin>

      <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000, display: 'flex', gap: 12 }}>
        <Button onClick={onCancel} size="large" shape="round">Cancel</Button>
        <Button
          onClick={handleSave}
          type="primary"
          size="large"
          shape="round"
          disabled={isDsrLogPqtUpdating || !currentDsrLog?.id || isDsrLogPqtLoading}
          loading={isDsrLogPqtUpdating}
        >
          Save & Next
        </Button>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentDsrLog: get(state, 'dsrReducer.currentDsrLog'),
  dsrLogPqt: get(state, 'projectPqtReportsReducer.dsrLogPqt', { pqt: null, elements: [], toDate: null }),
  isDsrLogPqtLoading: get(state, 'projectPqtReportsReducer.isDsrLogPqtLoading', false),
  isDsrLogPqtUpdating: get(state, 'projectPqtReportsReducer.isDsrLogPqtUpdating', false),
  lastDsrLogPqtUpdateSuccess: get(state, 'projectPqtReportsReducer.lastDsrLogPqtUpdateSuccess', null),
});

const mapDispatchToProps = {
  onFetchDsrLogPqt: fetchDsrLogPqt,
  onUpdateDsrLogPqt: updateDsrLogPqt,
  onCreateDsrLog: createDsrLog,
};

export default connect(mapStateToProps, mapDispatchToProps)(PqtActualValuesPage);
